import { useEffect, useState, useRef, useMemo } from 'react';
import { tokens } from "../../theme";
import Header from "../../components/Header";
import {
    useTheme, Box, Container, Grid, Card, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody, Typography, Paper, Autocomplete,
    ToggleButton, ToggleButtonGroup, Popper, CircularProgress, MenuItem
} from '@mui/material';

import { AreaSeries, createChart, ColorType, LineSeries, CrosshairMode, PriceScaleMode } from 'lightweight-charts';

import { __fetching_data__ } from '../../components/api';
import { date } from 'yup';
import { showLoader, hideLoader } from '../../components/Preloading2';

function createSeries(arr, selectedYear, chartInstanceRefs, seriesSetting, color, serieInstanceRefs, key, seriesType, seriesParam, idx) {
    const data = prepareData(arr, selectedYear, seriesType);
    // const series chartInstanceRefs.current[idx].addLineSeries(seriesSetting.mainSeries1);
    const series = chartInstanceRefs.current[idx].addLineSeries(seriesSetting);
    series.setData(data);
    chartInstanceRefs.current[idx].timeScale().fitContent();
    series.applyOptions({ color, lastValueVisible: false, visible: false });
    serieInstanceRefs.current[seriesParam][key] = series;
}

const compareDatetime = (a, b, field = 'DateTime') => {
    const datetimeA = new Date(a[field]);
    const datetimeB = new Date(b[field]);
    return datetimeA - datetimeB;
}

const filterByRange = (data, range) => {
    if (range === 'ALL') return data;

    const currentDate = new Date();
    let fromDate;

    switch (range) {
        case '3M':
            fromDate = new Date(currentDate);
            fromDate.setMonth(currentDate.getMonth() - 3);
            break;
        case '6M':
            fromDate = new Date(currentDate);
            fromDate.setMonth(currentDate.getMonth() - 6);
            break;
        case '1Y':
            fromDate = new Date(currentDate);
            fromDate.setFullYear(currentDate.getFullYear() - 1);
            break;
        case 'YTD':
            fromDate = new Date(currentDate.getFullYear(), 0, 1); // Jan 1st of current year
            break;
        default:
            console.warn(`Unsupported range: ${range}`);
            return data;
    }

    const filteredData = data
        .filter(item => {
            const itemDate = new Date(item.Date || item.date);
            return itemDate >= fromDate && itemDate <= currentDate;
        }
        )

    return filteredData;
};

const generateData = (data, date_columm = 'DateTime', field) => {
    const timeZoneOffset = 7 * 60 * 60 * 1000;
    const cdata = data.map(d => {
        let time = timeToTz(d[date_columm], 'Asia/Bangkok')

        const dd = new Date(d[date_columm])
        const year = dd.getFullYear();
        const month = String(dd.getMonth() + 1).padStart(2, '0'); // Add 1 because months are zero-indexed
        const day = String(dd.getDate()).padStart(2, '0');

        let time2 = `${year}-${month}-${day}`;

        return { time: time2, value: parseFloat(d[field]) || 0 }
    });
    return cdata;
}

function timeToTz(originalTime, timeZone) {
    const zonedDate = new Date(new Date(originalTime).toLocaleString('en-US', { timeZone }));
    return zonedDate.getTime() / 1000;
}

const generateUniqueRandomColors = (count) => {
    const colors = [
        "#FFC300", // Bright Yellow
        "#8BC34A", // Light Green
        "#A8E6CF", // Mint Green
        "#C9CBCF", // Light Grey
        "#4BC0C0", // Light Teal
        "#009688", // Teal
        "#FF6384", // Light Red
        "#36A2EB", // Light Blue
        "#FFCE56", // Soft Yellow
        "#9966FF", // Light Purple
        "#FF9F40", // Light Orange
        "#FFD3B6", // Peach
        "#FFAAA5", // Soft Red
        "#D1C4E9", // Lavender
        "#B3E5FC", // Baby Blue
        "#DCEDC8", // Pale Green
        "#FFF9C4", // Pale Yellow
        "#FFECB3"  // Light Amber
    ];

    const usedColors = new Set();

    const getRandomColor = () => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    };

    // Function to shuffle array
    function shuffleArray(array) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    const shuffledColors = shuffleArray(colors);

    while (shuffledColors.length < count) {
        //const color = getRandomColor();
        if (!usedColors.has(colors)) {
            usedColors.add(colors);
            colors.push(colors);
        }
    }

    return colors;
};

const prepareData = (arr, year, valueKey) => {
    const values = filterByRange(arr, year).sort((a, b) => compareDatetime(a, b, 'date'));
    const gen = generateData(values, 'date', valueKey);
    // Clean duplicate dates
    return Object.values(gen.reduce((acc, item) => {
        acc[item.time] = item;
        return acc;
    }, {}));
};

const PetroleumComponent_v4 = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const isEmpty = (obj) => {
        return Object.keys(obj).length === 0;
    }

    //####################  Create Setup ####################
    const chart_names = ['CLOSE', 'EX-REFIN', 'Marketing Margin', 'Oil Fund', 'CLOSE', 'EX-REFIN', 'Marketing Margin', 'Oil Fund'];
    const charts_options = [
        {
            width: 500,
            height: 30,
            crosshair: {
                mode: 1
            },
            timeScale: {
                visible: true,
                borderColor: theme.palette.divider,
            },
            localization: {
                locale: "en-US"
            },
            layout: {
                attributionLogo: false, // ปิดโลโก้ด้านมุม
                background: { 
                    type: 'solid', 
                    color: theme.palette.background.default 
                },
                textColor: theme.palette.text.primary,
            },
            grid: {
                vertLines: { color: theme.palette.divider },
                horzLines: { color: theme.palette.divider },
            },
            rightPriceScale: {

                scaleMargins: {
                    top: 0.3,
                    bottom: 0.2,
                },
                borderVisible: true,
                drawTicks: false,
            },
            lastValueVisible: false,

        }, {
            width: 500,
            height: 30,
            crosshair: {
                mode: 1
            },
            timeScale: {
                visible: true,
                borderColor: theme.palette.divider,
            },
            localization: {
                locale: "en-US"
            },
            layout: {
                attributionLogo: false, // ปิดโลโก้ด้านมุม
                background: { 
                    type: 'solid', 
                    color: theme.palette.background.default 
                },
                textColor: theme.palette.text.primary,
            },
            grid: {
                vertLines: { color: theme.palette.divider },
                horzLines: { color: theme.palette.divider },
            },
            rightPriceScale: {
                scaleMargins: {
                    top: 0.3,
                    bottom: 0.2,
                },
                borderVisible: false,
                drawTicks: true,
            },
            lastValueVisible: false
        }, {
            width: 500,
            height: 30,
            crosshair: {
                mode: 1
            },
            timeScale: {
                visible: true,
                borderColor: theme.palette.divider,
            },
            localization: {
                locale: "en-US"
            },
            layout: {
                attributionLogo: false, // ปิดโลโก้ด้านมุม
                background: { 
                    type: 'solid', 
                    color: theme.palette.background.default 
                },
                textColor: theme.palette.text.primary,
            },
            grid: {
                vertLines: { color: theme.palette.divider },
                horzLines: { color: theme.palette.divider },
            },
            rightPriceScale: {
                scaleMargins: {
                    top: 0.3,
                    bottom: 0.2,
                },
                borderVisible: false,
                drawTicks: true,
            },
            lastValueVisible: false
        }, {
            width: 500,
            height: 300,
            crosshair: {
                mode: 1
            },
            timeScale: {
                visible: true,
                borderColor: theme.palette.divider,
            },
            localization: {
                locale: "en-US"
            },
            layout: {
                attributionLogo: false, // ปิดโลโก้ด้านมุม
                background: { 
                    type: 'solid', 
                    color: theme.palette.background.default 
                },
                textColor: theme.palette.text.primary,
            },
            grid: {
                vertLines: { color: theme.palette.divider },
                horzLines: { color: theme.palette.divider },
            },
            rightPriceScale: {
                scaleMargins: {
                    top: 0.3,
                    bottom: 0.2,
                },
                borderVisible: false,
                drawTicks: true,
            },
            lastValueVisible: false
        }
    ]

    const serieInstanceRefs = useRef({ close: {}, ex_refin: {}, mkt_margin: {}, oil_fund: {} });
    const [seriesSetting, setSeriesSetting] = useState({
        'priceSeries': {
            color: "#ff9966",
            lineWidth: 3,
            priceLineVisible: false,
            lastValueVisible: false
        },
        'mainSeries2': {
            color: "#33ffcc",
            lineWidth: 3,
            priceLineVisible: false,
        },
        'mainSeries3': {
            color: "#ff9966",
            lineWidth: 3,
            priceLineVisible: false,
        },
        'mainSeries4': {
            color: "#66ffcc",
            lineWidth: 3,
            priceLineVisible: false,
        },
    });

    function getCrosshairDataPoint(series, param) {
        if (!param.time) {
            return null;
        }
        const dataPoint = param.seriesData.get(series);
        return dataPoint || null;
    }

    // function syncCrosshair1(chart, series, dataPoint) {
    //     if (dataPoint) {
    //         chart.setCrosshairPosition(dataPoint.value, dataPoint.time, series);
    //         return;
    //     }
    //     chart.clearCrosshairPosition();
    // }

    function syncCrosshair(chart, series, dataPoint) {
        if (!series) {
            chart.clearCrosshairPosition();
            return;
        }

        if (dataPoint && dataPoint.value != null && dataPoint.time != null) {
            // แปลง time เป็น UTCTimestamp (วินาที)
            const ts =
                typeof dataPoint.time === "number"
                    ? dataPoint.time
                    : Math.floor(new Date(dataPoint.time).getTime() / 1000);

            try {
                chart.setCrosshairPosition(dataPoint.value, ts, series);
            } catch (err) {
                console.warn("Crosshair sync skipped:", err);
                chart.clearCrosshairPosition();
            }
        } else {
            chart.clearCrosshairPosition();
        }
    }



    //####################  Create Chart container ####################
    const resChartRef = useRef(null);
    const resToolsRef = useRef(null);
    const [gridHeight, setGridHeight] = useState(0);
    const [chartsReady, setChartsReady] = useState(false);

    useEffect(() => {
        if (!resChartRef.current) return;
        const height = resChartRef.current.getBoundingClientRect().height;
        setGridHeight(height);

        const remainingHeight = `calc(100vh - ${90}px)`;
        resChartRef.current.style.height = remainingHeight;

    }, []);

    const [chartContainers, setChartContainers] = useState([]);
    const chartInstanceRefs = useRef([]);

    const handleAddChartContainer = (element) => {
        if (!chartContainers.includes(element) && element) {
            setChartContainers((prevContainers) => [...prevContainers, element]);
        }
    };

    const memoizedChartContainers = useMemo(() => chartContainers, [chartContainers]);
    const uniqueRandomColors = useRef([]);

    useEffect(() => {
        const content = document.getElementsByClassName('content')[0];
        if (!content) return;
        // const resizeHandler = () => {
        //     chartInstanceRefs.current.forEach((chart, index) => {
        //         if (chart) {
        //             chart.resize(chartContainers[index].clientWidth, chartContainers[index].clientHeight);
        //         }
        //     })
        // };
        // const resizeObserver = new ResizeObserver(resizeHandler);
        // resizeObserver.observe(content);
        const chartAnnotation = (chart, text) => {
            chart.applyOptions({
                watermark: {
                    visible: true,
                    fontSize: 30,
                    horzAlign: 'center',
                    vertAlign: 'center',
                    color: theme.palette.chart.watermark,
                    // color: 'rgba(255, 255, 255, 0.6)',
                    text: text,
                },
                rightPriceScale: { visible: true },
                leftPriceScale: {
                    visible: true,
                    mode: PriceScaleMode.Normal,
                    autoScale: false,
                    invertScale: false,
                    alignLabels: false,
                    minValue: -1,
                    maxValue: 1,
                },
            });
        }
        const createChartInstances = () => {
            chartContainers.forEach((container, index) => {
                const chart = createChart(container, charts_options[index ? index : 0]);
                chartAnnotation(chart, chart_names[index])
                chartInstanceRefs.current[index] = chart;
                chart.resize(chartContainers[index].clientWidth, resChartRef.current.clientHeight / 2);

            });
            setChartsReady(true);
            // window.addEventListener('resize', resizeHandler);
            return () => {
                // window.removeEventListener('resize', resizeHandler);
                chartInstanceRefs.current.forEach((chart) => {
                    if (chart) {
                        chart.remove();
                    }
                });
            };
        };
        createChartInstances();
        return () => {
            // handleParamsQuery();
            get_data();
            // resizeObserver.disconnect();
        };
    }, [memoizedChartContainers]);


    const [paramsQuery, setParamsQuery] = useState({
        symbol: "",
        queryType: 3,
    });

    //#################### Select and set Oil Type ####################
    const [optionsOilType, setOpeionsOilType] = useState([]);
    const [selectedOptionsOilType, setSelectedOptionsOilType] = useState([]);

    const handleSelectionChangeOilType = (event, newSelectedOptions) => {
        setSelectedOptionsOilType(newSelectedOptions);
    };

    useEffect(() => {
        setVisibleSeries();
    }, [selectedOptionsOilType]);


    //#################### Main Data ####################
    const [groupByPetroleumName, setGroupByPetroleumName] = useState({});
    const [symbolPrice, setSymbolPrice] = useState({});
    const [groupedDataState, setGroupedDataState] = useState({});


    //#################### Query Data ####################
    const [isSearch, setIsSearch] = useState(false);

    const handleParamsQuery = async (symbol, queryType) => {
        if (!chartInstanceRefs.current[0]) {
            return;
        }
        try {
            if (symbol !== undefined || queryType !== undefined) {
                setParamsQuery(prev => ({
                    ...prev,
                    symbol: symbol ?? prev.symbol,
                    queryType: queryType ?? prev.queryType
                }));
            }

        } catch (e) {

        }
    };

    useEffect(() => {
        if (isSearch) {
            clearAllSeries();
            get_data();
        }
    }, [isSearch]);

    const get_data = async (symbol, queryType) => {
        try {
            // console.log('Quering.... :', paramsQuery);
            const response = await __fetching_data__({ url: `${window.config.base_api_url}/petroleum_report?${new URLSearchParams(paramsQuery)}`, method: "GET" });
            if (!response.status) return;

            let groupedDataOilType = groupByPetroleumName;
            let sortedDataPrice = symbolPrice;

            if (isEmpty(groupedDataOilType) && response.data2) {
                const oneMonthAgo = new Date();
                oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 2);

                groupedDataOilType = response.data2.reduce((group, product) => {
                    const name = product.name.trim();
                    group[name] = group[name] || [];
                    group[name].push(product);
                    return group;
                }, {});

                groupedDataOilType = Object.fromEntries(
                    Object.entries(groupedDataOilType).filter(([_, arr]) =>
                        arr.some(product => new Date(product.date) >= oneMonthAgo)
                    )
                );
                setGroupByPetroleumName(groupedDataOilType);
                uniqueRandomColors.current = generateUniqueRandomColors(Object.keys(groupedDataOilType).length);
            }

            if (response.data) {
                sortedDataPrice = [...response.data].sort((a, b) => compareDatetime(a, b, 'Date'));
                setSymbolPrice(sortedDataPrice);
            }
        } catch (error) {
            console.error("Error fetching petroleum report:", error);
        }
    }

    const setData2Charts = () => {
        try {
            const optionOilKeys = Object.keys(groupByPetroleumName);
            setOpeionsOilType(optionOilKeys);

            const priceData = filterByRange(symbolPrice, selectedYear);

            const priceSeries = chartInstanceRefs.current[0].addLineSeries(seriesSetting.priceSeries);
            priceSeries.setData(generateData(priceData, 'Date', 'price'));
            serieInstanceRefs.current.close['close'] = priceSeries;

            optionOilKeys.forEach((key, idx) => {
                const color = uniqueRandomColors.current[idx];
                const arr = groupByPetroleumName[key];

                createSeries(arr, selectedYear, chartInstanceRefs, seriesSetting.mainSeries2, color, serieInstanceRefs, key, 'ex_refin', 'ex_refin', 1); // ex_refin
                createSeries(arr, selectedYear, chartInstanceRefs, seriesSetting.mainSeries3, color, serieInstanceRefs, key, 'marketing', 'mkt_margin', 2); // mkt_margin
                createSeries(arr, selectedYear, chartInstanceRefs, seriesSetting.mainSeries4, color, serieInstanceRefs, key, 'oil_fund', 'oil_fund', 3); // oil_fund
            });

            synchronizeCharts();
            setVisibleSeries();
            setIsSearch(false);
        } catch (error) {
            console.error("Error setData2Charts:", error);
        }
    }

    const setVisibleSeries = () => {
        try {
            optionsOilType.forEach((key, value) => {
                serieInstanceRefs.current.ex_refin[key].applyOptions({
                    visible: false
                });
                serieInstanceRefs.current.mkt_margin[key].applyOptions({
                    visible: false
                });
                serieInstanceRefs.current.oil_fund[key].applyOptions({
                    visible: false
                });
            });
        } catch (error) {

        }
        try {
            selectedOptionsOilType.forEach((key, value) => {
                serieInstanceRefs.current.ex_refin[key].applyOptions({
                    visible: true
                });
                serieInstanceRefs.current.mkt_margin[key].applyOptions({
                    visible: true
                });
                serieInstanceRefs.current.oil_fund[key].applyOptions({
                    visible: true
                });
            });
        } catch (error) {

        }
    }

    const synchronizeCharts1 = () => {
        try {
            const keys_series = Object.keys(serieInstanceRefs.current);
            const filteredCharts = chartInstanceRefs.current.filter((_, idx) => [1, 2, 3].includes(idx));
            console.log(1111, filteredCharts)
            filteredCharts.forEach((currentChart, currentIdx) => {
                currentChart.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                    filteredCharts.forEach((otherChart, otherIdx) => {
                        if (currentIdx !== otherIdx) {
                            otherChart.timeScale().setVisibleLogicalRange(timeRange);
                        }
                    });
                });
            });
            filteredCharts.forEach((currentChart, currentIdx) => {
                currentChart.subscribeCrosshairMove(param => {
                    const firstKey = Object.keys(serieInstanceRefs.current[keys_series[currentIdx + 1]])[0];
                    const dataPoint = getCrosshairDataPoint(serieInstanceRefs.current[keys_series[currentIdx + 1]][firstKey], param);
                    if (dataPoint !== undefined && dataPoint !== null) {
                        filteredCharts.forEach((otherChart, otherIdx) => {
                            if (currentIdx !== otherIdx) {
                                Object.keys(serieInstanceRefs.current[keys_series[otherIdx + 1]]).forEach(key_other_serie => {
                                    if (Object.hasOwnProperty.call(serieInstanceRefs.current[keys_series[otherIdx + 1]], key_other_serie)) {
                                        syncCrosshair(
                                            otherChart,
                                            serieInstanceRefs.current[keys_series[otherIdx + 1]][key_other_serie],
                                            dataPoint
                                        );
                                    }
                                });
                            }
                        });
                    }
                });
            });
        } catch (errSyncChart) {
            console.error("Error errSyncChart:", errSyncChart);
        }
    }

    const synchronizeCharts2 = () => {
        try {
            const keys_series = Object.keys(serieInstanceRefs.current);
            // ใช้ทุก chart เลย (รวม index 0)
            const filteredCharts = chartInstanceRefs.current;

            console.log("Sync charts:", filteredCharts);

            // sync การเลื่อนช่วงเวลา
            filteredCharts.forEach((currentChart, currentIdx) => {
                currentChart.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                    filteredCharts.forEach((otherChart, otherIdx) => {
                        if (currentIdx !== otherIdx) {
                            otherChart.timeScale().setVisibleLogicalRange(timeRange);
                        }
                    });
                });
            });

            // sync crosshair
            filteredCharts.forEach((currentChart, currentIdx) => {
                currentChart.subscribeCrosshairMove(param => {
                    const firstKey = Object.keys(
                        serieInstanceRefs.current[keys_series[currentIdx]]
                    )[0];

                    const dataPoint = getCrosshairDataPoint(
                        serieInstanceRefs.current[keys_series[currentIdx]][firstKey],
                        param
                    );

                    if (dataPoint !== undefined && dataPoint !== null) {
                        filteredCharts.forEach((otherChart, otherIdx) => {
                            if (currentIdx !== otherIdx) {
                                Object.keys(
                                    serieInstanceRefs.current[keys_series[otherIdx]]
                                ).forEach(key_other_serie => {
                                    if (
                                        Object.hasOwnProperty.call(
                                            serieInstanceRefs.current[keys_series[otherIdx]],
                                            key_other_serie
                                        )
                                    ) {
                                        syncCrosshair(
                                            otherChart,
                                            serieInstanceRefs.current[keys_series[otherIdx]][key_other_serie],
                                            dataPoint
                                        );
                                    }
                                });
                            }
                        });
                    }
                });
            });
        } catch (errSyncChart) {
            console.error("Error errSyncChart:", errSyncChart);
        }
    };

    const synchronizeCharts3 = () => {
        try {
            const keys_series = Object.keys(serieInstanceRefs.current);
            const filteredCharts = chartInstanceRefs.current;

            // sync การเลื่อนช่วงเวลา
            filteredCharts.forEach((currentChart, currentIdx) => {
                currentChart.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                    filteredCharts.forEach((otherChart, otherIdx) => {
                        if (currentIdx !== otherIdx) {
                            otherChart.timeScale().setVisibleLogicalRange(timeRange);
                        }
                    });
                });
            });

            // sync crosshair
            filteredCharts.forEach((currentChart, currentIdx) => {
                currentChart.subscribeCrosshairMove(param => {
                    const seriesGroup = serieInstanceRefs.current[keys_series[currentIdx]];
                    if (!seriesGroup) return; // ไม่มี series

                    const firstKey = Object.keys(seriesGroup)[0];
                    const dataPoint = getCrosshairDataPoint(seriesGroup[firstKey], param);

                    if (dataPoint !== undefined && dataPoint !== null) {
                        filteredCharts.forEach((otherChart, otherIdx) => {
                            if (currentIdx !== otherIdx) {
                                const otherSeriesGroup = serieInstanceRefs.current[keys_series[otherIdx]];
                                if (!otherSeriesGroup) return; // ไม่มี series

                                Object.keys(otherSeriesGroup).forEach(key_other_serie => {
                                    const serie = otherSeriesGroup[key_other_serie];
                                    // เช็คว่ามี data จริง ๆ ก่อน sync
                                    if (serie && serie.data && serie.data.length > 0) {
                                        syncCrosshair(otherChart, serie, dataPoint);
                                    }
                                });
                            }
                        });
                    }
                });
            });
        } catch (errSyncChart) {
            console.error("Error errSyncChart:", errSyncChart);
        }
    };

    const synchronizeCharts = () => {
        try {
            const keys_series = Object.keys(serieInstanceRefs.current);
            const filteredCharts = chartInstanceRefs.current;

            // sync การเลื่อนช่วงเวลา
            filteredCharts.forEach((currentChart, currentIdx) => {
                currentChart.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                    filteredCharts.forEach((otherChart, otherIdx) => {
                        if (currentIdx !== otherIdx) {
                            otherChart.timeScale().setVisibleLogicalRange(timeRange);
                        }
                    });
                });
            });

            // sync crosshair
            filteredCharts.forEach((currentChart, currentIdx) => {
                currentChart.subscribeCrosshairMove(param => {
                    const seriesGroup = serieInstanceRefs.current[keys_series[currentIdx]];
                    if (!seriesGroup) return; // ไม่มี series

                    const firstKey = Object.keys(seriesGroup)[0];
                    const dataPoint = getCrosshairDataPoint(seriesGroup[firstKey], param);

                    if (dataPoint !== undefined && dataPoint !== null) {
                        filteredCharts.forEach((otherChart, otherIdx) => {
                            if (currentIdx !== otherIdx) {
                                const otherSeriesGroup = serieInstanceRefs.current[keys_series[otherIdx]];
                                if (!otherSeriesGroup) return;

                                Object.keys(otherSeriesGroup).forEach(key_other_serie => {
                                    const serie = otherSeriesGroup[key_other_serie];
                                    if (serie) {
                                        // ไม่ต้องเช็ค serie.data เพราะ lightweight-charts ไม่ expose data แบบนั้น
                                        syncCrosshair(otherChart, serie, dataPoint);
                                    }
                                });
                            }
                        });
                    }
                });
            });
        } catch (errSyncChart) {
            console.error("Error errSyncChart:", errSyncChart);
        }
    };


    const clearAllSeries = () => {
        const chartRef = chartInstanceRefs.current;
        const serieRef = serieInstanceRefs.current;

        ['close', 'ex_refin', 'mkt_margin', 'oil_fund'].forEach((type, idx) => {
            const seriesMap = serieRef[type];
            Object.values(seriesMap).forEach(series => {
                try {
                    chartRef[idx]?.removeSeries(series);
                } catch (e) {
                    console.warn(`Failed to remove series for ${type}`, e);
                }
            });
            serieRef[type] = {}; // reset reference
        });
    };

    useEffect(() => {
        setData2Charts();
    }, [groupByPetroleumName, symbolPrice]);

    //####################  Filter by date range ####################
    const [selectedYear, setSelectedYear] = useState('3M');
    const handleYearChange = (event, newValue) => {
        setSelectedYear(newValue);
    };


    //#################### Select, Set, Change Symbol ####################
    const [symbols, setSymbols] = useState([]);
    const [selectedSymbol, setSelectedSymbol] = useState(null);

    useEffect(() => {
        const fetchSymbols = async () => {
            try {
                showLoader();
                const response = await __fetching_data__({ url: `${window.config.base_api_url}/petroleum_symbol_list`, method: "GET" });
                if (response.status) {
                    response.data.forEach((val, index) => {
                        setSymbols((prevSymbols) => [...prevSymbols, val.SymbolName]);
                    });
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                hideLoader();
            }
        };
        fetchSymbols();
    }, []);

    const handleChangeSymbol = (e, selectedValue) => {
        const newParams = {
            symbol: selectedValue,
            queryType: 4,
        };
        setParamsQuery(prevData => ({
            ...prevData,
            ...newParams,
        }));
    }


    useEffect(() => {
        if (chartsReady) {
            clearAllSeries();
            get_data();
        }
    }, [selectedYear, chartsReady]);


    return (
        <Box m="20px">
            <Box display="block">
                <Grid container spacing={2}
                    sx={{
                        marginBottom: "1rem"
                    }}
                    ref={resToolsRef}
                >
                    <Grid item xs={4} md={2}>
                        <Autocomplete
                            id="symbol"
                            options={symbols}
                            autoComplete
                            autoHighlight
                            fullWidth
                            inputValue={paramsQuery.symbol}
                            onInputChange={handleChangeSymbol}
                            onChange={(event, newValue) => {
                                // setSelectedSymbol(newValue);
                                newValue ? setIsSearch(true) : setIsSearch(false);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Symbol"
                                    variant="outlined"
                                    required
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option, { index }) => (
                                <MenuItem {...props} key={`${option}-${index}`}>
                                    <Typography variant="inherit">{option}</Typography>
                                </MenuItem>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            multiple
                            options={optionsOilType}
                            value={selectedOptionsOilType}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Select oil type"
                                    placeholder="select oil type"
                                />
                            )}
                            size='small'
                            onChange={handleSelectionChangeOilType}
                        />
                    </Grid>

                    <Grid item xs={4} md={2}>
                        <ToggleButtonGroup
                            value={selectedYear}
                            exclusive
                            onChange={handleYearChange}
                            aria-label="Basic radio toggle button group"
                            size="small"
                            fullWidth
                        >
                            <ToggleButton value="3M" aria-label="1Y" sx={{ color: 'white' }}>
                                3M
                            </ToggleButton>
                            <ToggleButton value="6M" aria-label="3Y" sx={{ color: 'white' }}>
                                6M
                            </ToggleButton>
                            <ToggleButton value="1Y" aria-label="5Y" sx={{ color: 'white' }}>
                                1Y
                            </ToggleButton>
                            <ToggleButton value="YTD" aria-label="5Y" sx={{ color: 'white' }}>
                                YTD
                            </ToggleButton>
                            <ToggleButton value="ALL" aria-label="ALL" sx={{ color: 'white' }}>
                                MAX
                            </ToggleButton>
                        </ToggleButtonGroup>

                    </Grid>
                    <Grid item xs={4} md={2}>
                        {/* 
                        <Button variant="contained"
                            sx={{
                                backgroundColor: colors.blueAccent[500]
                            }}
                            type="button"
                            onClick={handleSearchSymbol}
                        >
                            Search
                        </Button>*/}
                    </Grid>
                </Grid>

                <Grid
                    ref={resChartRef}
                    container
                    spacing={0}
                    sx={{
                        color: '#ffffff', // เพิ่มสีตัวอักษรเป็นสีขาว

                        // marginBottom: "1rem",
                        // backgroundColor: "red",
                        height: 'calc(100vh - 120px)',
                        minHeight: '500px',
                    }}
                >
                    <Grid item xs={12} md={6} sx={{ padding: '2px', height: '50%' }}>
                        <div id="chart-close" style={{ border: `1px solid ${colors.grey[700]}`,borderRadius: '16px', overflow: 'hidden', height: '100%' }} ref={(element) => handleAddChartContainer(element)}></div>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ padding: '2px', height: '50%' }}>
                        <div id="chart-ex-refin" style={{ border: `1px solid ${colors.grey[700]}`,borderRadius: '16px', overflow: 'hidden', height: '100%' }} ref={(element) => handleAddChartContainer(element)}></div>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ padding: '2px', height: '50%' }}>
                        <div id="chart-marketing-margin" style={{ border: `1px solid ${colors.grey[700]}`,borderRadius: '16px', overflow: 'hidden', height: '100%' }} ref={(element) => handleAddChartContainer(element)}></div>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ padding: '2px', height: '50%' }}>
                        <div id="chart-oil-fund" style={{border: `1px solid ${colors.grey[700]}`,borderRadius: '16px', overflow: 'hidden', height: '100%' }} ref={(element) => handleAddChartContainer(element)}></div>
                    </Grid>
                </Grid>

            </Box>
        </Box>
    )

}
export default PetroleumComponent_v4;